import { useEffect, useState } from "react";
import { VenuePairSelector } from "../components/VenuePairSelector.tsx";
import { EffectiveSpreadPanel } from "../panels/EffectiveSpreadPanel.tsx";
import { DepthView } from "../panels/DepthView.tsx";
import { bindingKey, fetchBindings, type Binding } from "../bindings.ts";
import { useIdentity } from "../components/LoginGate.tsx";

type Section = "spread" | "depth";

const SECTIONS: { id: Section; label: string; hint: string }[] = [
  { id: "spread", label: "Spread", hint: "quoted, effective & realized spread, capture, adverse selection" },
  { id: "depth", label: "Depth", hint: "band-bounded book depth (±0.5/1/2%)" },
];

// sidebar link styling — mirrors the dark palette used across the cards
const linkStyle = (active: boolean): React.CSSProperties => ({
  display: "block",
  width: "100%",
  textAlign: "left",
  padding: "8px 12px",
  marginBottom: 4,
  fontSize: 13,
  lineHeight: 1.3,
  cursor: "pointer",
  borderRadius: 6,
  border: `1px solid ${active ? "rgba(102,153,255,0.35)" : "transparent"}`,
  background: active ? "rgba(102,153,255,0.16)" : "transparent",
  color: active ? "#cfe0ff" : "#8a93a6",
});

export function AnalyticsTab() {
  const identity = useIdentity();
  const [scoped, setScoped] = useState<Binding[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [binding, setBinding] = useState<Binding | null>(null);
  const [section, setSection] = useState<Section>("spread");

  // Server-scoped roster: /api/v1/bindings returns only this session's
  // markets (operators see all clients). Refetch on identity change so a
  // re-login rescopes without an unmount.
  useEffect(() => {
    let cancelled = false;
    setScoped(null);
    setLoadError(null);
    setBinding(null);
    fetchBindings()
      .then((rows) => {
        if (!cancelled) setScoped(rows);
      })
      .catch((e: unknown) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [identity.username, identity.client_id]);

  if (loadError) {
    return (
      <div style={{ color: "#e08585", padding: 32, fontSize: 13 }}>
        Failed to load markets: {loadError}
      </div>
    );
  }
  if (scoped === null) {
    return (
      <div style={{ color: "#8a93a6", padding: 32, fontSize: 13 }}>
        Loading markets…
      </div>
    );
  }

  // Defensive: if the selected binding falls outside the session's scope
  // (identity changed without unmount), snap back to the first own market.
  const active =
    binding && scoped.some((b) => bindingKey(b) === bindingKey(binding))
      ? binding
      : scoped[0];

  if (!active) {
    return (
      <div style={{ color: "#8a93a6", padding: 32, fontSize: 13 }}>
        No markets are bound to this account yet.
      </div>
    );
  }

  return (
    <div className="tab">
      <div className="toolbar">
        <VenuePairSelector value={active} onChange={setBinding} options={scoped} />
      </div>

      <div className="sidelayout" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <nav
          className="sidenav"
          style={{
            flex: "0 0 180px",
            position: "sticky",
            top: 8,
            paddingTop: 4,
          }}
          aria-label="Analytics sections"
        >
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              style={linkStyle(section === s.id)}
              onClick={() => setSection(s.id)}
              title={s.hint}
            >
              {s.label}
            </button>
          ))}
        </nav>

        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          {section === "spread" && <EffectiveSpreadPanel binding={active} />}
          {section === "depth" && <DepthView binding={active} />}
        </div>
      </div>
    </div>
  );
}
